import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  DangerousGoodForm,
  DangerousGoodFormSchema,
  DangerousGoodFormValues,
} from 'forms/DangerousGoodsForm'
import {
  DangerousGood,
  DangerousGoodInsertType,
  DangerousGoodSetType,
  UserRole,
} from 'gql.generated'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useSelection } from 'hooks/useSelection'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import { DangerousGoodsList } from './AdminDangerousGoodsList'
import {
  ReadDangerousGoodsQueryVariables,
  useCreateDangerousGoodMutation,
  useDeleteDangerousGoodMutation,
  useReadDangerousGoodsQuery,
  useUpdateDangerousGoodMutation,
} from './AdminDangerousGoodsView.generated'

const DangerousGoodsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createDangerousGood] = useCreateDangerousGoodMutation()
  const [updateDangerousGood] = useUpdateDangerousGoodMutation()
  const [deleteDangerousGood] = useDeleteDangerousGoodMutation()
  const [formType, setFormType] = React.useState<FormType>()
  const [selectedItemId, setSelectedItemId] = React.useState<
    string | undefined
  >()

  const { loading, data, error, refetch } = useReadDangerousGoodsQuery()

  const items =
    data && data.dangerousGoods
      ? data.dangerousGoods.filter((x) => !x._deleted)
      : []

  const [selection, selectedItems] = useSelection<DangerousGood>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const handleSubmit = async (
    values: DangerousGoodFormValues,
    formikActions: FormikActions<DangerousGoodFormSchema>
  ) => {
    formikActions.setSubmitting(true)

    if (formType === FormType.insert) {
      await createDangerousGood({
        variables: { input: values as DangerousGoodInsertType },
      })
    } else if (formType === FormType.update) {
      await updateDangerousGood({
        variables: {
          id: selectedItemId,
          update: values as DangerousGoodSetType,
        },
      })
    } else {
      throw new Error(
        ' Custom: Error while DangerousGood create or update. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch({} as ReadDangerousGoodsQueryVariables)
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteDangerousGood({
        variables: { ids },
      })
    }
    refetch({} as ReadDangerousGoodsQueryVariables)
    hideDialog()
  }

  /** commands */
  useCreateCommand(
    'dangerousGood',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({} as DangerousGoodInsertType)
      },
    },
    [setFormType, showPanel]
  )
  useEditCommand(
    { name: 'dangerousGood', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], [
            'modifiedBy',
            'createdBy',
            'created',
            'modified',
            'id',
            '__typename',
          ]) as DangerousGoodSetType
        )
      },
    },
    [selectedItems, setFormType, setSelectedItemId, showPanel]
  )
  // useDeleteCommand(
  //   { name: 'orderId', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <DangerousGoodsList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                setSelectedItemId(selectedItems[0]._id)
                showPanel(
                  omitDeep(selectedItems[0], [
                    'createdBy',
                    'created',
                    'modifiedBy',
                    'modified',
                    'id',
                    '__typename',
                  ]) as DangerousGoodSetType
                )
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        validationSchema={new DangerousGoodFormSchema()}
        onSubmit={handleSubmit}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.dangerousGood.edit')
            : t('model.dangerousGood.create')
        }
      >
        <DangerousGoodForm formType={formType || FormType.insert} />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const DangerousGoodsView: React.FC = () => {
  return (
    <ViewAuthorizer
      roles={[UserRole.ResourceManager, UserRole.SystemAdministrator]}
    >
      <StickyPane>
        <AppCommandBar />
        <DangerousGoodsListView />
      </StickyPane>
    </ViewAuthorizer>
  )
}
